import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';

import {
  Add,
  Edit,
  Delete,
  Search,
  Build,
  CheckCircle,
  Warning,
  Error,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { equipmentService, Equipment as EquipmentType } from '../services/equipmentService';

// Using Equipment type from service
type Equipment = EquipmentType;

const Equipment: React.FC = () => {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState<Equipment | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch equipment data from API
  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await equipmentService.getAll({
        search: searchTerm,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      // equipmentService.getAll now returns the equipment array directly
      setEquipment(Array.isArray(response) ? response : []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch equipment');
      console.error('Error fetching equipment:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refetch when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        fetchEquipment();
      } else {
        fetchEquipment();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleDeleteClick = (equipment: Equipment) => {
    setEquipmentToDelete(equipment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!equipmentToDelete) return;

    try {
      await equipmentService.delete(equipmentToDelete._id || equipmentToDelete.id!);
      setSnackbar({ open: true, message: 'Equipment deleted successfully', severity: 'success' });
      fetchEquipment(); // Refresh the list
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to delete equipment', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setEquipmentToDelete(null);
    }
  };

  const handleStatusChange = async (equipment: Equipment, newStatus: Equipment['status']) => {
    try {
      await equipmentService.updateStatus(equipment._id || equipment.id!, newStatus);
      setSnackbar({ open: true, message: 'Equipment status updated successfully', severity: 'success' });
      fetchEquipment(); // Refresh the list
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update status', severity: 'error' });
    }
  };

  const normalizeStatus = (s: any) => {
    const t = String(s || '').toLowerCase();
    if (t === 'available') return 'available';
    if (t === 'in use' || t === 'in-use') return 'in-use';
    if (t === 'maintenance') return 'maintenance';
    if (t === 'repair' || t === 'out-of-service') return 'out-of-service';
    return t as Equipment['status'];
  };

  const getStatusColor = (status: Equipment['status'] | string) => {
    switch (normalizeStatus(status)) {
      case 'available':
        return 'success';
      case 'in-use':
        return 'primary';
      case 'maintenance':
        return 'warning';
      case 'out-of-service':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: Equipment['status'] | string) => {
    switch (normalizeStatus(status)) {
      case 'available':
        return <CheckCircle />;
      case 'in-use':
        return <Build />;
      case 'maintenance':
        return <Warning />;
      case 'out-of-service':
        return <Error />;
      default:
        return undefined;
    }
  };

  // Ensure equipment is an array before using array methods
  const equipmentArray = Array.isArray(equipment) ? equipment : [];
  
  // Equipment is already filtered by the API based on search term
  const filteredEquipment = equipmentArray;
  
  const totalValue = equipmentArray.reduce((sum, item) => sum + (Number((item as any).currentValue ?? item.value ?? 0) || 0), 0);
  const availableCount = equipmentArray.filter(item => normalizeStatus(item.status) === 'available').length;
  const inUseCount = equipmentArray.filter(item => normalizeStatus(item.status) === 'in-use').length;
  const maintenanceCount = equipmentArray.filter(item => normalizeStatus(item.status) === 'maintenance').length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading equipment...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchEquipment}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Equipment Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/equipment/create')}
        >
          Add Equipment
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Equipment
            </Typography>
            <Typography variant="h5" component="div">
              {equipmentArray.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Available
            </Typography>
            <Typography variant="h5" component="div" color="success.main">
              {availableCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              In Use
            </Typography>
            <Typography variant="h5" component="div" color="primary.main">
              {inUseCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Value
            </Typography>
            <Typography variant="h5" component="div">
              ${totalValue.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search equipment..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Equipment Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Serial Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Next Maintenance</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEquipment.map((item) => (
              <TableRow key={item._id || item.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">{item.name}</Typography>
                </TableCell>
                <TableCell>{item.type}</TableCell>
                <TableCell>{item.model}</TableCell>
                <TableCell>{item.serialNumber}</TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(item.status)}
                    label={String(item.status).replace('-', ' ').toUpperCase()}
                    color={getStatusColor(item.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{item.location}</TableCell>
                <TableCell>{item.assignedTo || '-'}</TableCell>
                <TableCell>
                  {item.nextMaintenance ? new Date(item.nextMaintenance).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>${Number((item as any).currentValue ?? item.value ?? 0).toLocaleString()}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/equipment/${item._id || item.id}`)}
                    title="View Equipment"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/equipment/${item._id || item.id}`)}
                    title="Edit Equipment"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleDeleteClick(item)}
                    title="Delete Equipment"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredEquipment.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No equipment found
          </Typography>
          <Typography color="textSecondary">
            {searchTerm ? 'Try adjusting your search terms' : 'Start by adding your first equipment'}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{equipmentToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Equipment;